import { useQuery } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import { LaneType } from 'common-types';
import { Assignable } from 'domains-work-order-type-contracts';
import { getChannelGroupRoles } from '../../../graphql/query/getChannelGroupRoles';
import { getDisplayName } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { convertToUUID } from 'uuid-encoding';

import getUsersByWorkOrderTeams from '../../../graphql/workOrders/getUsersByWorkOrderTeams';

/**
 * Alias for Assignable type for backwards compatibility.
 *
 * @deprecated Use {@link Assignable} from `domains-work-order-type-contracts` directly instead.
 */
export type AdminUser = Assignable;

/**
 * Hook to fetch assignable members (users and teams) for work orders.
 *
 * @param channelId - The channel ID to fetch assignable members for
 * @param groupRoleIds - Optional array of group role IDs to filter assignable members
 * @returns Tuple containing [assignableUsers, loading, assignableTeams]
 *
 * @public
 */
export const useAssignableMembers = (
  channelId?: string,
  groupRoleIds: LaneType.UUID[] = []
): [Assignable[], boolean, Assignable[]] => {
  const { data, loading: groupRolesLoading } = useQuery(getChannelGroupRoles, {
    variables: {
      id: channelId,
    },
    skip: !channelId,
    fetchPolicy: 'cache-and-network',
  });
  const groupRoles = cloneDeep(data?.channel?.groupRoles || []);

  const assignableTeams = groupRoles.filter(gr =>
    gr?.permissions?.includes(
      PERMISSION_KEYS.PERMISSION_WORK_ORDERS_SERVICE_REQUEST_ASSIGNABLE_TEAM
    )
  );

  const groupRoleIdsToFetch =
    groupRoleIds.length > 0 ? groupRoleIds : assignableTeams.map(gr => gr._id);

  const { data: usersData, loading: usersLoading } = useQuery(
    getUsersByWorkOrderTeams,
    {
      skip: !groupRoleIdsToFetch || groupRoleIdsToFetch.length === 0,
      variables: {
        groupRoleIds: groupRoleIdsToFetch,
        search: {},
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const uniqueIDs = new Set();
  const adminUsers: Assignable[] =
    usersData?.workOrderUsersByGroupRoles?.items
      ?.map(({ user }: any) => {
        if (!uniqueIDs.has(user._id)) {
          uniqueIDs.add(user._id);

          return {
            label: getDisplayName(user.profile),
            displayName: getDisplayName(user.profile),
            value: user._id,
          };
        }

        return {};
      })
      .filter((obj: any) => Object.keys(obj).length !== 0)
      .sort((a: any, b: any) => {
        return a.label.localeCompare(b.label);
      }) ?? [];

  const adminTeams: Assignable[] = assignableTeams
    .map(gr => ({
      label: gr.name,
      displayName: gr.name,
      value: gr._id ? convertToUUID(gr._id) : gr._id,
    }))
    .sort((a, b) => {
      return a.label.localeCompare(b.label);
    });

  return [adminUsers, groupRolesLoading || usersLoading, adminTeams];
};
